import { Form, FormItemProps, Select, SelectProps } from "antd";
import { useEffect, useState } from "react";
import { useInnerDataByFormName } from "./DataForm";


export type DataOptionProps<T> = {
    valueFromData?: (data: T | undefined) => any,
    labelFromData?: (data: T | undefined) => string | undefined,
    valueToData?: (value: string, data?: T) => T,
    labelToData?: (label: string, data?: T) => T,
};

export type DataSelectProps<T,TFilter> = SelectProps<any> & {
    label?: string | string[],
    selected?: T | T[],
    initialData?: T[] | (() => T[]),
    searchFilter?: TFilter,
    searchByInput?: boolean,
    canCreateNew?: boolean,
    search?: (text:string, filter?: TFilter) => Promise<T[]>,
    onInputTextChange?: (text: string) => void,
    dataOptionsProps?: DataOptionProps<T>,
};


export function DataSelect<T,TFilter>(props: DataSelectProps<T,TFilter>) {

    const {
        selected,
        initialData,
        searchFilter,
        searchByInput,
        canCreateNew,
        search,
        onInputTextChange,
        dataOptionsProps,
        ...selectProps
    } = props;

      
    const [ data, setData ] = useState<T[]>(props.initialData || []);
    const [ inputText, setInputText ] = useState<string>('');
    const filter = props.searchFilter;

    useEffect(() => { 
        search && search(searchByInput && inputText || '', filter).then((result) => {
            setData(result);
        });
    }, [ search, searchByInput, inputText, filter ]);

    const valueFromData = props.dataOptionsProps?.valueFromData || ((data: any) => data?.id );
    const labelFromData = props.dataOptionsProps?.labelFromData || valueFromData;
    const optionFromData = (data: T) => (
        <Select.Option key={valueFromData(data)} value={valueFromData(data)}>{labelFromData(data)}</Select.Option>
    );

    const isSearchTextInData = () => {
        return data.find((i) => labelFromData(i) === inputText);
    }

    const handleChange = (value: any) => {
        if (value!=NULL_VALUE) {
            setInputText('');
        }
    }

    const handleSearch = (newValue: string) => {
        setInputText(newValue); // Will trigger search
        //props.onInputTextChange && props.onInputTextChange(newValue);
    };

    // Be sure to include the selected option if search data is not loaded yet
    const selectedData = selected && (Array.isArray(selected) ? selected : [selected]);
    const valueData = props.value && (Array.isArray(props.value) ? props.value : [props.value]);
    const labelData = props.label && (Array.isArray(props.label) ? props.label : [props.label]);
    const existing = <>
        { 
            (data && data.length && data.map(optionFromData)) ||
            (selectedData && selectedData.length && selectedData.map(optionFromData)) ||
            (valueData && valueData.length && valueData.map((value: any, index: number) => {
                const label = labelData && labelData[index];
                return <Select.Option key={value} value={value}>{label}</Select.Option>
            }))
        }
    </>;

    const createNew = canCreateNew && inputText && !isSearchTextInData() && <Select.OptGroup label="Create new">
        <Select.Option key={NULL_VALUE} value={NULL_VALUE}>{inputText}</Select.Option>
    </Select.OptGroup> || null;

    return (
        <Select
            showSearch={ searchByInput || canCreateNew || false }
            defaultActiveFirstOption={false}
            filterOption={false}
            onSearch={setInputText}
            //onChange={handleChange}
            notFoundContent={null}
            allowClear={true}
            {...selectProps}
        >
            
            {/* Create new option if enabled */}
            {createNew}

            {/* Existing options */}
            {createNew && <Select.OptGroup label="Or select existing">{existing}</Select.OptGroup> || existing }

        </Select>
    );

}

const NULL_VALUE = '__NULL__';

export const ValueFormItemProps = function<T>(props: DataOptionProps<T>) { // }: DataFormItemFuncsType<T> {
    const valueFromData = props.valueFromData || ((data: any) => {
        if (data?.id===undefined) return undefined;
        return data.id || NULL_VALUE;
    });
    const labelFromData = props.labelFromData || valueFromData;
    const valueToData = props.valueToData || ((value: string, data: any) => {
        if (!value) return value;
        return { id: NULL_VALUE==value? null : value }
    });
    const labelToData = props.labelToData || ((label: string, data: any) => (data));

    return {
        getValueFromEvent: (values: any, options:any) => {
            if (!values) return values;
            if (Array.isArray(values)) {
                let result = [];
                for (let i = 0; i < values.length; i++) {
                    result.push(labelToData(options[i].children, valueToData(values[i], {} as T)));
                }
                return result;
            } else {
                return labelToData(options.children, valueToData(values, {} as T));
            }
        },
        getValueProps: (objs: T | T[]) => {
            if (objs === undefined) return undefined;
            let result: any = {};
            if (objs && Array.isArray(objs)) {
                result['value'] = objs.map((obj) => valueFromData(obj));
                result['label'] = objs.map((obj) => labelFromData(obj));
            } else {
                result['value'] = valueFromData(objs);
                result['label'] = labelFromData(objs);
            }
            return result;
        }
    }
}

export type DataSelectFormItemProps<T,TFilter> = FormItemProps<any> & {
    //name: string | string[],
    mode?: 'multiple' | undefined, // | 'tags' | 'combobox' - Avoid using tax because it allows to create new values
    dataOptionsProps: DataOptionProps<T>
    dataSelectProps: DataSelectProps<T,TFilter>
};

export const DataFormItem = function<T>(props: 
    FormItemProps<any> & {
        dataOptionsProps: DataOptionProps<T>,
    }
) {
    
    const { dataOptionsProps, ...formItemProps } = props;

    return (
        <Form.Item className="input"
            {...formItemProps}
            {... ValueFormItemProps(props.dataOptionsProps) }
        >
            {/* props.dataSelectType({
                ...props.dataOptionsProps,
                ...pick(props, dataSelectProps),
                selected: useInnerDataByFormName(props.name)
            }) */}
        </Form.Item>
    )
}